import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOWN_ARROW, ESCAPE } from '@angular/cdk/keycodes';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   ElementRef,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Optional,
   Output,
   Pipe,
   PipeTransform,
   Renderer2,
   ViewContainerRef,
   ViewEncapsulation,
   isDevMode,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { CanUpdateErrorState, defaultSelectConnectedPositions, mixinErrorState } from '@lcs/shared/core';
import { LcsMultiSelectComponent } from '@lcs/shared/multi-select';
import { takeUntil } from 'rxjs/operators';

class LcsMultiSelectTriggerBase {
   constructor(
      public _defaultErrorStateMatcher: ErrorStateMatcher,
      public _parentForm: NgForm,
      public _parentFormGroup: FormGroupDirective,
      public ngControl: NgControl
   ) {}
}

const _LcsMultiSelectTriggerMixinBase = mixinErrorState(LcsMultiSelectTriggerBase);

export type LcsChipMultiSelectTextContentFn<T> = (item: T | null | undefined) => string;

@Pipe({
   name: 'lcsChipDisplay',
   standalone: false,
})
export class ChipDisplayPipe<T> implements PipeTransform {
   transform(item: T, displayFn: LcsChipMultiSelectTextContentFn<T> | null | undefined): string {
      if (displayFn) {
         return displayFn(item);
      } else {
         return '';
      }
   }
}

@Component({
   // eslint-disable-next-line @angular-eslint/component-selector
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   host: {
      'class': 'lcs-multi-select-trigger',
      'role': 'combobox',
      'aria-haspopup': 'dialog',
      '[attr.tabindex]': 'disabled ? -1 : 0',
      '[id]': 'id',
      '[attr.aria-expanded]': 'isOpen() || null',
      '[attr.aria-controls]': 'isOpen() ? panel.id : null',
      '(click)': 'onClick($event)',
      '(keydown)': 'onKeydown($event)',
      '(focus)': 'onFocus()',
      '(blur)': 'onBlur()',
   },
   providers: [{ provide: MatFormFieldControl, useExisting: LcsChipMultiSelectTriggerComponent }],
   selector: 'lcs-chip-multi-select-trigger',
   templateUrl: './chip-multi-select-trigger.component.html',
   standalone: false,
})
export class LcsChipMultiSelectTriggerComponent<T>
   extends _LcsMultiSelectTriggerMixinBase
   implements
      ControlValueAccessor,
      OnDestroy,
      OnInit,
      DoCheck,
      CanUpdateErrorState,
      MatFormFieldControl<T[] | readonly T[] | null | undefined>
{
   @Input('for') panel: LcsMultiSelectComponent<T>;

   @Input() editButton: MatButton;

   @Input()
   set value(value: T[] | readonly T[] | null | undefined) {
      this._value = value;
      this.panel?.setSelectionByValue(value);
      this.changeDetectorRef.markForCheck();
   }

   get value(): T[] | readonly T[] | null | undefined {
      return this._value;
   }

   private _value: T[] | readonly T[] | null | undefined;

   @Input() placeholder: string;

   @Input() id: string;

   @Input()
   set disabled(disabled: boolean) {
      this._disabled = coerceBooleanProperty(disabled);
      if (this.panel) {
         this.panel.disabled = this._disabled;
      }
   }

   get disabled(): boolean {
      return this._disabled;
   }

   private _disabled = false;

   @Input()
   set required(required: boolean) {
      this._required = coerceBooleanProperty(required);
   }

   get required(): boolean {
      return this._required;
   }

   private _required: boolean;

   @Input()
   set getDisplayText(getDisplayText: LcsChipMultiSelectTextContentFn<T> | null | undefined) {
      if (getDisplayText) {
         this._getDisplayText = getDisplayText;
      }
   }

   get getDisplayText(): LcsChipMultiSelectTextContentFn<T> | null | undefined {
      return this._getDisplayText;
   }

   private _getDisplayText: LcsChipMultiSelectTextContentFn<T> | null | undefined;

   @Output() readonly valueChange = new EventEmitter<T[] | readonly T[] | null | undefined>();

   ariaDescribedBy: string | null = null;

   controlType = 'lcs-multi-select';

   empty = false;

   get focused(): boolean {
      return this.isOpen() || this.elementFocused;
   }

   shouldLabelFloat = true;

   private elementFocused = false;

   private overlayRef?: OverlayRef;

   private panelContent?: TemplatePortal;

   private onChange?: (value: readonly T[] | T[] | null | undefined) => void;

   private onTouched?: () => void;

   constructor(
      private overlay: Overlay,
      private elementRef: ElementRef<HTMLButtonElement>,
      private viewContainerRef: ViewContainerRef,
      @Optional() ngControl: NgControl,
      @Optional() parentForm: NgForm,
      @Optional() parentFormGroup: FormGroupDirective,
      defaultErrorStateMatcher: ErrorStateMatcher,
      @Optional() private matFormField: MatFormField,
      private changeDetectorRef: ChangeDetectorRef,
      private renderer: Renderer2
   ) {
      super(defaultErrorStateMatcher, parentForm, parentFormGroup, ngControl);
      if (ngControl) {
         ngControl.valueAccessor = this;
      }
   }

   onRemove(item: T) {
      this.value = this.value?.filter((v) => {
         return v !== item;
      });
      if (this.onChange) {
         this.onChange(this.value);
      }
   }

   setDescribedByIds(ids: string[]): void {
      this.ariaDescribedBy = ids.length > 0 ? ids.join(' ') : null;
   }

   onContainerClick(_event: MouseEvent): void {
      if (this.editButton) {
         return;
      }
      this.focus();
      this.togglePanel();
   }

   onEdit(): void {
      this.focus();
      this.togglePanel();
   }

   writeValue(value: T[] | null | undefined): void {
      this.value = value;
   }

   registerOnChange(fn: (value: readonly T[] | T[] | null | undefined) => void): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
   }

   setDisabledState(disabled: boolean): void {
      this._disabled = disabled;
   }

   focus(): void {
      if (!this.focused) {
         this.elementFocused = true;
         this.elementRef.nativeElement.focus();
      }
   }

   onFocus(): void {
      this.elementFocused = true;
   }

   onBlur(): void {
      this.elementFocused = false;
      if (this.onTouched) {
         this.onTouched();
      }
      this.stateChanges.next();
   }

   ngOnInit(): void {
      if (isDevMode() && !this.panel) {
         throw new Error('lcsMultiSelectTrigger requires a MultiSelectPanelComponent');
      }

      if (this.editButton) {
         this.renderer.listen(this.editButton._elementRef.nativeElement, 'click', (_) => {
            this.onEdit();
         });
      }

      this.panel.disabled = this._disabled;
   }

   ngOnDestroy(): void {
      this.overlayRef?.dispose();
   }

   ngDoCheck() {
      if (this.ngControl) {
         if (!this.isOpen()) {
            this.updateErrorState();
         }
      }
   }

   onKeydown(event: KeyboardEvent): void {
      if (!this._disabled) {
         if (!this.isOpen() && event.keyCode === DOWN_ARROW) {
            this.open();
         }
      }
   }

   onClick(event: MouseEvent): void {
      if (this.editButton) {
         return;
      }
      this.togglePanel();
      // stop propagation to prevent MatFormField container clicks from immediately closing the panel again
      event.stopPropagation();
   }

   togglePanel(): void {
      if (this.isOpen()) {
         this.panel.onCancelled();
         this.close();
      } else {
         this.open();
      }
   }

   open() {
      if (this.isOpen() || this._disabled) {
         return;
      }

      if (!this.overlayRef) {
         this.overlayRef = this.overlay.create(this.getOverlayConfig());

         this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
            if (event.keyCode === ESCAPE) {
               this.panel.onCancelled();
            }
         });
      }

      this.panelContent = this.panelContent ?? new TemplatePortal(this.panel.panelTemplate, this.viewContainerRef);

      const detachments = this.overlayRef.detachments();

      this.panel.submitted.pipe(takeUntil(detachments)).subscribe((event) => {
         const value = event.selectedItems;
         this.value = value;
         this.valueChange.emit(value);
         if (this.onChange) {
            this.onChange(value);
         }
         this.close();

         this.changeDetectorRef.markForCheck();
      });

      this.panel.cancelled.pipe(takeUntil(detachments)).subscribe(() => {
         this.close();
      });

      this.panel.setSelectionByValue(this.value);

      this.overlayRef.attach(this.panelContent);

      this.panel.onPanelOpened();
   }

   close() {
      if (this.isOpen()) {
         // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
         this.overlayRef!.detach();
         this.focus();
      }
   }

   isOpen(): boolean {
      return this.overlayRef?.hasAttached() ?? false;
   }

   private getOverlayConfig(): OverlayConfig {
      return new OverlayConfig({
         positionStrategy: this.getOverlayPositionStrategy(),
         scrollStrategy: this.overlay.scrollStrategies.reposition(),
         panelClass: 'lcs-multi-select-panel',
         hasBackdrop: true,
         backdropClass: 'cdk-overlay-transparent-backdrop',
      });
   }

   private getOverlayPositionStrategy() {
      const overlayOrigin = this.matFormField ? this.matFormField.getConnectedOverlayOrigin() : this.elementRef;
      return this.overlay.position().flexibleConnectedTo(overlayOrigin).withPositions(defaultSelectConnectedPositions);
   }

   /* eslint-disable @typescript-eslint/member-ordering */
   /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
   static ngAcceptInputType_disabled: BooleanInput;

   static ngAcceptInputType_required: BooleanInput;
}
